import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Button,
  Snackbar,
  Alert,
  Slide,
  Grid,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Switch,
  Collapse,
  Tabs,
  Tab,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Slider,
  InputLabel,
} from "@mui/material";
import {
  ContentCopy,
  CalendarMonth,
  Link,
  CheckCircle,
  Info,
  Google,
  CalendarToday,
  Schedule,
  ContactMail,
  PlayCircleOutline,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
  CreditCardOff,
  Groups,
  PersonAdd,
  Close,
  Add,
  Delete,
  Edit,
  Person,
  PriorityHigh,
  Settings,
  AccessTime,
  NightsStay,
  Search
} from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import axios from "axios";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from "constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const getLoomEmbedUrl = (shareUrl) => {
  if (!shareUrl) return null;
  return shareUrl.replace('/share/', '/embed/');
};
const timezones = Intl.supportedValuesOf("timeZone");
console.log("Supported Timezones:", timezones);
const LOOM_SHARE_URLS = {
  'google-calendar': process.env.REACT_APP_GOOGLE_CALENDAR_LOOM_URL,
  'calendly': process.env.REACT_APP_CALENDLY_LOOM_URL,
  'zoho-calendar': process.env.REACT_APP_ZOHO_CALENDAR_LOOM_URL,
  'team-scheduling': process.env.REACT_APP_TEAM_SCHEDULING_LOOM_URL,
};

const DEFAULT_PROVIDER = "google-calendar";
const TEAM_SCHEDULING_PROVIDER = "team-scheduling";

const calendarProviders = [
  {
    id: "google-calendar",
    name: "Google Calendar",
    logo: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg",
    description: "Sync calendar events and schedule meetings seamlessly",
    color: "#219297",
    icon: <Google />,
    hasVideo: true,
    type: "single-user",
  },
  {
    id: "calendly",
    name: "Calendly",
    logo: "https://www.bing.com/th/id/OIP.isWc_EiA6Ms4U-FNJlRwEwHaHa?w=193&h=211&c=8&rs=1&qlt=90&o=6&cb=ucfimg1&pid=3.1&rm=2&ucfimg=1",
    description: "Professional scheduling and meeting management",
    color: "#00A4FF",
    icon: <Schedule />,
    hasVideo: true,
    type: "single-user",
  },
  {
    id: "zoho-calendar",
    name: "Zoho Calendar",
    logo: "https://www.zoho.com/branding/images/zoho-logo.png",
    description: "Appointment scheduling with Zoho integration",
    color: "#E42526",
    icon: <CalendarToday />,
    hasVideo: true,
    type: "single-user",
  },
  {
    id: "contact-form",
    name: "Contact Form",
    logo: "",
    description: "Your contact page or form URL for inquiries",
    color: "#6B46C1",
    icon: <ContactMail />,
    hasVideo: false,
    type: "single-user",
  },
  {
    id: TEAM_SCHEDULING_PROVIDER,
    name: "Team Scheduling",
    logo: "",
    description: "Round-robin scheduling with multiple team members",
    color: "#FF6900",
    icon: <Groups />,
    hasVideo: true,
    type: "team",
  },
];

const providerInstructions = {
  "google-calendar": [
    { title: "Sign in to Google Calendar", content: "Use a Google Workspace account to access appointment scheduling features." },
    { title: "Create Appointment Schedule", content: "On the left panel, click 'Create' → 'Appointment schedule'." },
    { title: "Configure Settings", content: "Set title, availability, duration, and meeting location preferences." },
    { title: "Save & Share", content: "Save the schedule, then click 'Share' → 'Copy booking page link'." },
    { title: "Paste URL", content: "The copied link is your public appointment booking URL." },
  ],
  "calendly": [
    { title: "Sign in to Calendly", content: "Access your Calendly dashboard." },
    { title: "Create Event Type", content: "Create a new event type (15 min, 30 min, custom, etc.)." },
    { title: "Configure Settings", content: "Set availability, location, and form questions." },
    { title: "Publish & Share", content: "Publish the event type and copy the 'Share your link' URL." },
    { title: "Paste URL", content: "Use the copied Calendly link as your booking URL." },
  ],
  "zoho-calendar": [
    { title: "Sign in to Zoho Calendar", content: "Access your Zoho Calendar dashboard." },
    { title: "Navigate to Settings", content: "Right-click the calendar from the left pane and click Edit, or go to Settings > Calendar > My Calendars" },
    { title: "Enable public access", content: "Under Sharing and permissions > Public access, enable public access for the calendar." },
    { title: "Configure permissions", content: "Choose if you want others to View only free/busy status or View event details." },
    { title: "Customize interface", content: "Click Customize near Schedule Appointment to customize your interface." },
    { title: "Choose language and color", content: "Choose desired language and color for your interface." },
    { title: "Copy the iframe source URL", content: "Copy the src URL from the iframe under Schedule Appointment section." },
    { title: "Paste the URL here", content: "Paste the copied URL in the field above." },
  ],
  "contact-form": [
    { title: "Ensure Accessibility", content: "Make sure the page is publicly accessible and not behind a login." },
    { title: "Form Functionality", content: "Ensure the form is working properly and can receive messages." },
    { title: "Paste URL", content: "Paste your contact page URL in the field on the left." },
  ],
  "team-scheduling": [
    { title: "Connect Google Calendar", content: "Enable Google Calendar integration first to access team scheduling." },
    { title: "Configure Team Members", content: "Add team members who have shared their calendars with you." },
    { title: "Set Scheduling Rules", content: "Configure working hours, slot duration, and booking rules." },
    { title: "Handle Night Shifts", content: "For night shifts (6 PM - 3 AM), set end time to next day hour." },
  ],
};

const providerExamples = {
  "google-calendar": "https://calendar.google.com/calendar/appointments/schedules/abc123xyz456\nhttps://calendar.app.google.com/calendar/schedule",
  "calendly": "https://calendly.com/yourname/event\nhttps://calendly.com/yourname/30min",
  "zoho-calendar": "https://calendar.zoho.com/book/yourname\nhttps://calendar.zoho.com/meeting/abc123",
  "contact-form": "https://yourwebsite.com/contact\nhttps://yourwebsite.com/contact-form",
  "team-scheduling": "https://yourdomain.com/api/public/booking/availability\n(Generated automatically)",
};

const providerPricing = {
  "google-calendar": "Free",
  "calendly": "Free",
  "zoho-calendar": "Included in Zoho Workplace paid plans",
  "contact-form": "Free (Your own website)",
  "team-scheduling": "Free",
};

const providerPlaceholders = {
  "google-calendar": "Enter your Google Calendar appointment schedule URL",
  "calendly": "Enter your Calendly event URL",
  "zoho-calendar": "Enter your Zoho Calendar booking URL",
  "contact-form": "Enter your contact page/form URL",
  "team-scheduling": "Team scheduling uses connected Google Calendar",
};

const providerLabels = {
  "google-calendar": "Google Calendar Booking URL",
  "calendly": "Calendly Event URL",
  "zoho-calendar": "Zoho Calendar Booking URL",
  "contact-form": "Contact Page/Form URL",
  "team-scheduling": "Team Scheduling Configuration",
};

const isNightShift = (startTime, endTime) => {
  if (!startTime || !endTime) return false;
  
  try {
    const [startHour] = startTime.split(':').map(Number);
    const [endHour] = endTime.split(':').map(Number);
    return endHour < startHour;
  } catch (e) {
    return false;
  }
};

const formatWorkingHours = (start, end) => {
  if (!start || !end) return '09:00 - 17:00';
  
  const isNight = isNightShift(start, end);
  return `${start} - ${end} ${isNight ? '(overnight)' : ''}`;
};

const formatWorkingDays = (days) => {
  if (!days || days.length === 0) return 'None';
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && 
      days.includes('monday') && 
      days.includes('tuesday') && 
      days.includes('wednesday') && 
      days.includes('thursday') && 
      days.includes('friday')) {
    return 'Weekdays';
  }
  return days.map(day => day.slice(0, 3).toUpperCase()).join(', ');
};

const CustomInput = React.memo(({ 
  value, 
  onChange, 
  placeholder, 
  error, 
  helperText, 
  disabled, 
  startIcon, 
  endAdornment,
  type = "text",
  onKeyPress,
  className = "",
  fullWidth = true,
  style = {}
}) => {
  const inputRef = useRef(null);

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        width: fullWidth ? '100%' : 'auto',
        ...style
      }}
      className={className}
    >
      <Box
        onClick={handleFocus}
        sx={{
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'text',
          backgroundColor: disabled ? '#f5f5f5' : '#ffffff',
          border: `1px solid ${error ? '#d32f2f' : '#e0e0e0'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease',
          '&:hover': !disabled && {
            borderColor: error ? '#d32f2f' : '#219297',
            boxShadow: '0 2px 8px rgba(33, 146, 151, 0.1)',
          },
          '&:focus-within': {
            borderColor: error ? '#d32f2f' : '#219297',
            boxShadow: '0 0 0 3px rgba(33, 146, 151, 0.1)',
            outline: 'none',
          },
        }}
      >
        {startIcon && (
          <Box 
            sx={{ 
              position: 'absolute', 
              left: '16px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: error ? '#d32f2f' : '#666',
              display: 'flex',
              alignItems: 'center',
              zIndex: 1
            }}
          >
            {startIcon}
          </Box>
        )}
        
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            fontSize: '0.95rem',
            color: '#333',
            outline: 'none',
            padding: startIcon ? '0 0 0 40px' : '0',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.7 : 1,
          }}
        />
        
        {endAdornment && (
          <Box 
            sx={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {endAdornment}
          </Box>
        )}
      </Box>
      
      {helperText && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: error ? '#d32f2f' : '#666',
            marginTop: '4px',
            marginLeft: '4px',
            lineHeight: 1.4,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

const InstructionStep = React.memo(({ number, title, children, icon }) => (
  <Box sx={{ 
    mb: 2,
    display: 'flex',
    gap: 2,
    alignItems: 'flex-start'
  }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        minWidth: 28,
        borderRadius: '50%',
        backgroundColor: '#e0f7fa',
        color: '#219297',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '0.85rem',
        flexShrink: 0,
      }}
    >
      {icon || number}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography 
        variant="subtitle2"
        fontWeight="600" 
        sx={{ 
          color: '#333',
          mb: 0.5,
          fontSize: '0.9rem'
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: '#666',
          fontSize: '0.85rem',
          lineHeight: 1.5,
        }}
      >
        {children}
      </Typography>
    </Box>
  </Box>
));

const NightShiftBadge = ({ isNightShift, timeRange, size = 'small' }) => (
  <Box display="flex" alignItems="center" gap={0.5}>
    <Chip
      label={size === 'small' ? "🌙 Night" : "🌙 Night Shift"}
      size={size}
      sx={{
        fontSize: size === 'small' ? '0.65rem' : '0.75rem',
        height: size === 'small' ? '18px' : '24px',
        backgroundColor: '#4527a0',
        color: '#fff',
        '& .MuiChip-label': {
          padding: size === 'small' ? '0 6px' : '0 8px',
        }
      }}
    />
    {timeRange && (
      <Tooltip title={`Night shift hours: ${timeRange}`}>
        <InfoOutlined fontSize="small" sx={{ color: '#666', fontSize: '0.8rem' }} />
      </Tooltip>
    )}
  </Box>
);

const PriorityEditor = React.memo(({ member, saving, onUpdatePriority }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(member.priority || 5);

  const handleSave = async () => {
    await onUpdatePriority(member.calendar_id, editValue);
    setIsEditing(false);
  };

  const getPriorityLabel = (value) => {
    const priorityOptions = [
      { label: "Low", value: 10 },
      { label: "Medium", value: 5 },
      { label: "High", value: 1 }
    ];
    const option = priorityOptions.find(opt => opt.value === value);
    return option ? option.label : "Medium";
  };

  const getPriorityColor = (value) => {
    switch(value) {
      case 1: return "#d32f2f";
      case 5: return "#ed6c02";
      case 10: return "#2e7d32";
      default: return "#666666";
    }
  };

  const priorityOptions = [
    { label: "Low", value: 10 },
    { label: "Medium", value: 5 },
    { label: "High", value: 1 }
  ];

  if (isEditing) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: '#fff',
              borderRadius: '6px',
            }}
          >
            {priorityOptions.map((option) => (
              <MenuItem 
                key={option.value} 
                value={option.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {/* <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: getPriorityColor(option.value),
                  }}
                /> */}
                <Typography variant="body2">
                  {option.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ 
            minWidth: 'auto', 
            padding: '4px 8px',
            backgroundColor: '#219297',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#197f85',
            }
          }}
        >
          Save
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setIsEditing(false)}
          sx={{ minWidth: 'auto', padding: '4px 8px',color:"#000000" }}
        >
          Cancel
        </Button>
      </Box>
    );
  }
  
  return (
    <Chip 
      label={`${getPriorityLabel(member.priority)} (${member.priority})`}
      size="small"
      sx={{ 
        backgroundColor: `${getPriorityColor(member.priority)}15`,
        color: getPriorityColor(member.priority),
        fontSize: '0.7rem',
        fontWeight: 500,
        border: `1px solid ${getPriorityColor(member.priority)}30`,
        cursor: 'pointer',
      }}
      onClick={() => setIsEditing(true)}
    />
  );
});

const MemberWorkingHours = React.memo(({ 
  member, 
  editingMemberId, 
  editingMemberSettings, 
  setEditingMemberSettings,
  onUpdateMemberSettings,
  onStartEditing,
  onCancelEditing,
  saving 
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  if (editingMemberId === member.calendar_id) {
    const settings = editingMemberSettings[member.calendar_id] || {};
    const startTime = settings.working_hours_start || member.working_hours_start || '09:00';
    const endTime = settings.working_hours_end || member.working_hours_end || '17:00';
    const isNight = isNightShift(startTime, endTime);

    return (
      <Box sx={{ mt: 1, p: 2, backgroundColor: '#f0f7fa', borderRadius: '6px' }}>
        <Typography variant="caption" fontWeight="500" sx={{ color: '#219297', mb: 1, display: 'block' }}>
          Individual Working Hours
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" sx={{ color: '#666', minWidth: '60px' }}>
                Start:
              </Typography>
              <CustomInput
                type="time"
                value={startTime}
                onChange={(e) => setEditingMemberSettings(prev => ({
                  ...prev,
                  [member.calendar_id]: {
                    ...settings,
                    working_hours_start: e.target.value
                  }
                }))}
                fullWidth={false}
                style={{ flex: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" sx={{ color: '#666', minWidth: '60px' }}>
                End:
              </Typography>
              <CustomInput
                type="time"
                value={endTime}
                onChange={(e) => setEditingMemberSettings(prev => ({
                  ...prev,
                  [member.calendar_id]: {
                    ...settings,
                    working_hours_end: e.target.value
                  }
                }))}
                fullWidth={false}
                style={{ flex: 1 }}
              />
            </Box>
          </Grid>
          
          {/* {isNight && (
            <Grid item xs={12}>
              <Alert 
                severity="info" 
                icon={<InfoOutlined />}
                sx={{ 
                  backgroundColor: '#e3f2fd',
                  fontSize: '0.75rem',
                  py: 0.5,
                  '& .MuiAlert-message': { padding: '4px 0' }
                }}
              >
                <Typography variant="caption" sx={{ color: '#1565c0' }}>
                  ⚠️ Night shift detected ({startTime} → {endTime}). 
                  This means working hours span across midnight.
                </Typography>
              </Alert>
            </Grid>
          )} */}
          
          <Grid item xs={12}>
            <Typography variant="caption" fontWeight="500" sx={{ color: '#219297', mb: 1, display: 'block', mt: 1 }}>
              Working Days
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <Chip
                  key={day}
                  label={day.slice(0, 3).toUpperCase()}
                  size="small"
                  onClick={() => {
                    const days = [...(settings.working_days || member.working_days || [])];
                    if (days.includes(day)) {
                      setEditingMemberSettings(prev => ({
                        ...prev,
                        [member.calendar_id]: {
                          ...settings,
                          working_days: days.filter(d => d !== day)
                        }
                      }));
                    } else {
                      setEditingMemberSettings(prev => ({
                        ...prev,
                        [member.calendar_id]: {
                          ...settings,
                          working_days: [...days, day]
                        }
                      }));
                    }
                  }}
                  color={(settings.working_days || member.working_days || []).includes(day) ? 'primary' : 'default'}
                  sx={{
                    fontSize: '0.6rem',
                    height: '20px',
                    backgroundColor: (settings.working_days || member.working_days || []).includes(day) ? '#219297' : '#e0e0e0',
                    color: (settings.working_days || member.working_days || []).includes(day) ? '#fff' : '#666',
                  }}
                />
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#e1f8f2ff', borderRadius: '6px', border: '1px solid #b3ffd5ff' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" fontWeight="500" sx={{ color: '#007c6cff' }}>
                  <InfoOutlined fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Night Shift Instructions
                </Typography>
                <IconButton size="small" onClick={() => setShowAdvanced(!showAdvanced)}>
                  {showAdvanced ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
              
              <Collapse in={showAdvanced}>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#000000ff', display: 'block', mb: 1 }}>
                    For night shifts like 6 PM to 3 AM or 9 PM to 5 AM:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.5 } }}>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Set <strong>Start time</strong> to when shift begins
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Set <strong>End time</strong> to when shift ends the next day
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Example: 18:00 → 03:00 means working from 6 PM to 3 AM next day
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Select the days when the shift starts (Monday-Friday for Mon-Fri night shifts)
                      </Typography>
                    </li>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box display="flex" gap={1} justifyContent="flex-end" mt={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  onCancelEditing();
                  setShowAdvanced(false);
                }}
                sx={{ fontSize: '0.7rem',color: '#000000' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => onUpdateMemberSettings(member.calendar_id)}
                disabled={saving}
                sx={{ 
                  fontSize: '0.7rem',
                  backgroundColor: '#219297',
                  color:"#ffffff"
                }}
              >
                Save Hours
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
  
  const isNight = isNightShift(member.working_hours_start, member.working_hours_end);

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Hours: {formatWorkingHours(member.working_hours_start, member.working_hours_end)}
        </Typography>
        {isNight && (
          <NightShiftBadge isNightShift={true} size="small" />
        )}
        <Typography variant="caption" sx={{ color: '#666' }}>
          | Days: {formatWorkingDays(member.working_days)}
        </Typography>
      </Box>
      <Button
        size="small"
        startIcon={<Edit fontSize="small" />}
        onClick={() => onStartEditing(member)}
        sx={{ 
          mt: 0.5,
          fontSize: '0.65rem',
          padding: '2px 6px',
          minWidth: 'auto',
          color:"#219197"
        }}
      >
        Edit Hours
      </Button>
    </Box>
  );
});

const TeamManagementModal = React.memo(({ open, onClose, domainId, refreshData, initialTeamData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [teamMembers, setTeamMembers] = useState(initialTeamData || []);
  const [sharedCalendars, setSharedCalendars] = useState([]);
  const [config, setConfig] = useState(null);
  
  const [currentTimezone, setCurrentTimezone] = useState('UTC');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [timezones, setTimezones] = useState([]);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [isEditingTimezone, setIsEditingTimezone] = useState(false);
  const [timezonesLoaded, setTimezonesLoaded] = useState(false);
  
  const [newConfig, setNewConfig] = useState({
    slot_duration_minutes: 30,
    buffer_before_minutes: 5,
    buffer_after_minutes: 5,
    min_lead_time_hours: 2,
    max_booking_days: 60,
  });
  
  const [activeTab, setActiveTab] = useState('team-members');
  const [newMemberPriority, setNewMemberPriority] = useState(5);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  
  const [newMemberSettings, setNewMemberSettings] = useState({
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });

  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editingMemberSettings, setEditingMemberSettings] = useState({});

  const priorityOptions = [
    { label: "Low", value: 10 },
    { label: "Medium", value: 5 },
    { label: "High", value: 1 }
  ];

  const fetchTimezone = useCallback(async () => {
    if (!domainId) return;
    
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/domain/timezone`, {
        headers: { "DOMAIN-TOKEN": domainId }
      });
      
      if (res.data.success) {
        const timezone = res.data.data.timezone || 'UTC';
        setCurrentTimezone(timezone);
        setSelectedTimezone(timezone);
      }
    } catch (err) {
      console.error("Failed to fetch timezone:", err);
      setCurrentTimezone('UTC');
      setSelectedTimezone('UTC');
    }
  }, [domainId]);

  const updateTimezone = useCallback(async () => {
    if (!domainId || !selectedTimezone) return;
    
    try {
      setSaving(true);
      const res = await axios.patch(
        `${FAST_API_BASE_URL}/api/domain/timezone`,
        { timezone: selectedTimezone },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setCurrentTimezone(selectedTimezone);
        setIsEditingTimezone(false);
        setTimezoneSearch('');
        setSuccess("Timezone updated successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to update timezone");
      }
    } catch (err) {
      console.error("Failed to update timezone:", err);
      setError("Failed to update timezone. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [domainId, selectedTimezone]);

  useEffect(() => {
    const loadTimezones = () => {
      if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
        try {
          const supportedTimezones = Intl.supportedValuesOf("timeZone");
          const allTimezones = ['UTC', ...supportedTimezones.filter(tz => tz !== 'UTC')];
          setTimezones(allTimezones);
          setTimezonesLoaded(true);
        } catch (err) {
          console.error("Failed to get supported timezones:", err);
          setFallbackTimezones();
        }
      } else {
        setFallbackTimezones();
      }
    };

    const setFallbackTimezones = () => {
      const fallbackTimezones = [
        // Americas
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Toronto',
        'America/Vancouver',
        'America/Sao_Paulo',
        // Europe
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Madrid',
        'Europe/Rome',
        'Europe/Amsterdam',
        // Asia
        'Asia/Tokyo',
        'Asia/Shanghai',
        'Asia/Singapore',
        'Asia/Hong_Kong',
        'Asia/Seoul',
        'Asia/Dubai',
        // Australia/Pacific
        'Australia/Sydney',
        'Australia/Melbourne',
        'Pacific/Auckland',
        // Africa
        'Africa/Cairo',
        'Africa/Johannesburg',
      ];
      setTimezones(fallbackTimezones);
      setTimezonesLoaded(true);
    };

    loadTimezones();
  }, []);

  useEffect(() => {
    if (timezonesLoaded && currentTimezone && !selectedTimezone) {
      const exists = timezones.some(tz => tz === currentTimezone);
      setSelectedTimezone(exists ? currentTimezone : timezones[0] || 'UTC');
    }
  }, [timezonesLoaded, currentTimezone, timezones, selectedTimezone]);

  useEffect(() => {
    if (!open || !domainId) return;
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await fetchTimezone();
        
        const calendarsRes = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/writable-calendars`, {
          headers: { "DOMAIN-TOKEN": domainId }
        }).catch(() => ({ data: { success: false, data: { calendars: [] } } }));

        if (calendarsRes.data.success) {
          const calendars = calendarsRes.data.data.calendars || [];
          const processedCalendars = calendars.map(calendar => ({
            ...calendar,
            id: calendar.id || calendar.email,
            name: calendar.name || calendar.summary || calendar.email
          }));
          setSharedCalendars(processedCalendars);
        }

        const statusRes = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/status`, {
          headers: { "DOMAIN-TOKEN": domainId }
        }).catch(() => ({ data: { success: false, data: {} } }));

        if (statusRes.data.success) {
          const statusData = statusRes.data.data;
          
          if (statusData.team_members && statusData.team_members.length > 0) {
            setTeamMembers(statusData.team_members);
          } else if (initialTeamData) {
            setTeamMembers(initialTeamData);
          }
          
          if (statusData.config) {
            const configData = statusData.config;
            setConfig(configData);
            setNewConfig({
              slot_duration_minutes: configData.slot_duration_minutes || 30,
              buffer_before_minutes: configData.buffer_before_minutes || 5,
              buffer_after_minutes: configData.buffer_after_minutes || 5,
              min_lead_time_hours: configData.min_lead_time_hours || 2,
              max_booking_days: configData.max_booking_days || 60,
            });
          }
        }

      } catch (err) {
        console.error("Failed to fetch team data:", err);
        setError("Failed to load team data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, domainId, initialTeamData, fetchTimezone]);

  const getPriorityLabel = (value) => {
    const option = priorityOptions.find(opt => opt.value === value);
    return option ? option.label : "Medium";
  };

  const getPriorityColor = (value) => {
    switch(value) {
      case 1: return "#d32f2f";
      case 5: return "#ed6c02";
      case 10: return "#2e7d32";
      default: return "#666666";
    }
  };

  const handleAddTeamMember = async () => {
    if (!selectedCalendar) {
      setError("Please select a calendar first");
      return;
    }
    
    const calendar = availableCalendars.find(c => 
      c.id === selectedCalendar || 
      c.email === selectedCalendar ||
      String(c.id) === String(selectedCalendar)
    );
    
    if (!calendar) {
      setError("Selected calendar not found. Please select again.");
      return;
    }
    
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendar.id || calendar.email,
          priority: newMemberPriority,
          name: calendar.name || calendar.summary || calendar.email || calendar.id,
          enabled: true,
          working_hours_start: newMemberSettings.working_hours_start,
          working_hours_end: newMemberSettings.working_hours_end,
          working_days: newMemberSettings.working_days,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        const newMember = {
          calendar_id: calendar.id || calendar.email,
          priority: newMemberPriority,
          name: calendar.name || calendar.summary || calendar.email || calendar.id,
          email: calendar.email,
          enabled: true,
          working_hours_start: newMemberSettings.working_hours_start,
          working_hours_end: newMemberSettings.working_hours_end,
          working_days: newMemberSettings.working_days,
          is_night_shift: isNightShift(newMemberSettings.working_hours_start, newMemberSettings.working_hours_end),
        };
        
        setTeamMembers(prev => [...prev, newMember]);
        setSelectedCalendar('');
        setNewMemberSettings({
          working_hours_start: '09:00',
          working_hours_end: '17:00',
          working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
        });
        
        setSuccess("Team member added successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to add team member");
      }
    } catch (err) {
      console.error("Failed to add team member:", err);
      const errorMsg = err.response?.data?.message || "Failed to add team member. Please try again.";
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMemberSettings = async (memberId) => {
    const member = teamMembers.find(m => m.calendar_id === memberId);
    if (!member || !editingMemberSettings[memberId]) return;
    
    try {
      setSaving(true);
      const settings = editingMemberSettings[memberId];
      
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: memberId,
          priority: member.priority,
          name: member.name,
          enabled: true,
          working_hours_start: settings.working_hours_start,
          working_hours_end: settings.working_hours_end,
          working_days: settings.working_days,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.map(m => 
          m.calendar_id === memberId ? { 
            ...m, 
            ...settings,
            is_night_shift: isNightShift(settings.working_hours_start, settings.working_hours_end)
          } : m
        ));
        setEditingMemberId(null);
        setEditingMemberSettings({});
        setSuccess("Member settings updated successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to update member settings");
      }
    } catch (err) {
      console.error("Failed to update member settings:", err);
      setError("Failed to update member settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePriority = async (calendarId, priority) => {
    try {
      setSaving(true);
      const member = teamMembers.find(m => m.calendar_id === calendarId);
      if (!member) return;
      
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendarId,
          priority: priority,
          name: member.name,
          enabled: true,
          working_hours_start: member.working_hours_start || '09:00',
          working_hours_end: member.working_hours_end || '17:00',
          working_days: member.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.map(member => 
          member.calendar_id === calendarId ? { ...member, priority } : member
        ));
        setSuccess("Priority updated successfully!");
      } else {
        setError(res.data.message || "Failed to update priority");
      }
    } catch (err) {
      console.error("Failed to update priority:", err);
      setError("Failed to update priority. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTeamMember = async (calendarId) => {
    if (!calendarId) return;
    
    try {
      setSaving(true);
      const member = teamMembers.find(m => m.calendar_id === calendarId);
      if (!member) return;
      
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendarId,
          priority: member.priority,
          name: member.name,
          enabled: false,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.filter(member => member.calendar_id !== calendarId));
        setSuccess("Team member removed");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to remove team member");
      }
    } catch (err) {
      console.error("Failed to remove team member:", err);
      setError("Failed to remove team member. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const startEditingMember = (member) => {
    setEditingMemberId(member.calendar_id);
    setEditingMemberSettings({
      [member.calendar_id]: {
        working_hours_start: member.working_hours_start || '09:00',
        working_hours_end: member.working_hours_end || '17:00',
        working_days: member.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      }
    });
  };

  const cancelEditingMember = () => {
    setEditingMemberId(null);
    setEditingMemberSettings({});
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/config`,
        { config: newConfig },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setConfig(newConfig);
        setSuccess("Configuration saved successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Failed to save config:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const availableCalendars = useMemo(() => {
    return sharedCalendars.filter(calendar => {
      const isAlreadyMember = teamMembers.some(member => {
        const memberIdentifiers = [member.calendar_id, member.email, member.id].filter(Boolean);
        const calendarIdentifiers = [calendar.id, calendar.email, calendar.summary].filter(Boolean);
        return memberIdentifiers.some(memberId => 
          calendarIdentifiers.some(calendarId => 
            memberId && calendarId && memberId.toLowerCase() === calendarId.toLowerCase()
          )
        );
      });
      return !isAlreadyMember;
    });
  }, [sharedCalendars, teamMembers]);

const TimezoneDisplay = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [isHovering, setIsHovering] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [showTimezoneList, setShowTimezoneList] = useState(false);
  const searchInputRef = useRef(null);
  const listContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const getCurrentTime = (timezone) => {
    try {
      return new Date().toLocaleString('en-US', { 
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return 'Invalid timezone';
    }
  };

  const getTimezoneOffset = (timezone) => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset'
      });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(part => part.type === 'timeZoneName');
      return offsetPart ? offsetPart.value : '';
    } catch (e) {
      return '';
    }
  };

  const formatTimezoneForDisplay = (tz) => {
    if (!tz) return '';
    const parts = tz.split('/');
    if (parts.length > 1) {
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      return `${city} (${getTimezoneOffset(tz)})`;
    }
    return tz;
  };

  const formatTimezoneForMobile = (tz) => {
    if (!tz) return '';
    const parts = tz.split('/');
    if (parts.length > 1) {
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      return isMobile ? city : `${city} (${getTimezoneOffset(tz)})`;
    }
    return tz;
  };

  const filteredTimezones = useMemo(() => {
    if (!searchInput.trim()) return timezones;
    
    const searchTerm = searchInput.toLowerCase();
    return timezones.filter(tz => 
      tz.toLowerCase().includes(searchTerm) ||
      tz.toLowerCase().replace(/_/g, ' ').includes(searchTerm) ||
      formatTimezoneForDisplay(tz).toLowerCase().includes(searchTerm)
    );
  }, [timezones, searchInput]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    // Show list when user starts typing
    if (value.trim() && !showTimezoneList) {
      setShowTimezoneList(true);
    }
  };

  const handleSearchFocus = () => {
    setShowTimezoneList(true);
  };

  const handleTimezoneSelect = (tz) => {
    setSelectedTimezone(tz);
    setSearchInput(tz); // Fill search bar with selected timezone
    setShowTimezoneList(false); // Close the list after selection
    
    // Focus back on input after selection (optional)
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 10);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && filteredTimezones.length > 0) {
      handleTimezoneSelect(filteredTimezones[0]);
    }
    
    // Close list on Escape key
    if (e.key === 'Escape') {
      setShowTimezoneList(false);
      searchInputRef.current?.blur();
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setShowTimezoneList(true);
    searchInputRef.current?.focus();
  };

  // Close list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        listContainerRef.current &&
        !listContainerRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowTimezoneList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close list when pressing Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showTimezoneList) {
        setShowTimezoneList(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTimezoneList]);

  // Auto-close list when there are no results
  useEffect(() => {
    if (showTimezoneList && searchInput.trim() && filteredTimezones.length === 0) {
      const timer = setTimeout(() => {
        setShowTimezoneList(false);
      }, 2000); // Close after 2 seconds if no results
      return () => clearTimeout(timer);
    }
  }, [showTimezoneList, searchInput, filteredTimezones.length]);

  if (isEditingTimezone) {
    return (
      <Box 
        className="timezone-edit-container"
        ref={listContainerRef}
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          backgroundColor: '#f8fafa', 
          borderRadius: '10px',
          border: '1px solid #e0e0e0',
          mb: 3,
          position: 'relative'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <AccessTime sx={{ color: '#219297', fontSize: { xs: 16, sm: 18 } }} />
            <Typography 
              variant="subtitle2" 
              fontWeight="600" 
              sx={{ 
                color: '#333', 
                fontSize: { xs: '0.85rem', sm: '0.9rem' } 
              }}
            >
              Select Company Timezone
            </Typography>
          </Box>
          <Chip
            label="Required"
            size="small"
            sx={{ 
              fontSize: { xs: '0.6rem', sm: '0.65rem' },
              backgroundColor: '#21929715',
              color: '#219297',
              fontWeight: 500
            }}
          />
        </Box>
        
        <Box sx={{ mb: 2, position: 'relative' }}>
          {/* Search input with dropdown positioned right below it */}
          <Box sx={{ position: 'relative' }}>
            <CustomInput
              ref={searchInputRef}
              value={searchInput}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search timezones..."
              startIcon={<Search fontSize="small" />}
              style={{ marginBottom: '8px' }}
              endAdornment={
                searchInput ? (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ p: 0.5 }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => setShowTimezoneList(!showTimezoneList)}
                    sx={{ p: 0.5 }}
                  >
                    {showTimezoneList ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                )
              }
              autoFocus={!isMobile}
            />
            
            {searchInput && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  ml: 1,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                {filteredTimezones.length} {filteredTimezones.length === 1 ? 'timezone' : 'timezones'} found
              </Typography>
            )}
            
            {/* Dropdown list - Positioned absolutely right below the search input */}
            {showTimezoneList && (
              <Paper 
                ref={dropdownRef}
                sx={{ 
                  position: 'absolute',
                  top: '100%', // Position directly below the search input
                  left: 0,
                  right: 0,
                  maxHeight: { xs: 200, sm: 250 }, 
                  overflow: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  zIndex: 1300, // Higher z-index to ensure it's on top
                  mt: 0.5 // Small margin from search input
                }}
              >
                {!timezonesLoaded ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={20} sx={{ color: '#666', mb: 1 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        fontSize: { xs: '0.8rem', sm: '0.9rem' }
                      }}
                    >
                      Loading timezones...
                    </Typography>
                  </Box>
                ) : filteredTimezones.length > 0 ? (
                  <List sx={{ py: 0 }}>
                    {filteredTimezones.map((tz) => (
                      <ListItem
                        key={tz}
                        button
                        onClick={() => handleTimezoneSelect(tz)}
                        selected={selectedTimezone === tz}
                        sx={{
                          borderBottom: '1px solid #f0f0f0',
                          p: { xs: 1.5, sm: 2 },
                          '&:hover': {
                            backgroundColor: '#f0f7fa',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#21929710',
                            borderLeft: '3px solid #219297',
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ width: '100%' }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 500, 
                                    color: '#333',
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                    mb: { xs: 0.5, sm: 0 }
                                  }}
                                >
                                  {formatTimezoneForMobile(tz)}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#666',
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                  }}
                                >
                                  {getCurrentTime(tz)}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#999', 
                                  fontFamily: 'monospace', 
                                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                  display: 'block',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {tz}
                              </Typography>
                            </Box>
                          }
                        />
                        {selectedTimezone === tz && (
                          <CheckCircle sx={{ 
                            color: '#219297', 
                            fontSize: { xs: 14, sm: 16 },
                            ml: 1
                          }} />
                        )}
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Search sx={{ 
                      fontSize: { xs: 24, sm: 32 }, 
                      color: '#ccc', 
                      mb: 1 
                    }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#999',
                        fontSize: { xs: '0.85rem', sm: '0.9rem' }
                      }}
                    >
                      No timezones found matching "{searchInput}"
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#999', 
                        display: 'block', 
                        mt: 1,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' }
                      }}
                    >
                      Try a different search term
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
          
          {/* Selected timezone display - Comes AFTER the search input and dropdown area */}
          <Box sx={{ 
            mt: 2,
            p: { xs: 1, sm: 1.5 },
            backgroundColor: '#f0f7fa',
            borderRadius: '8px',
            border: '1px solid #e0f0f0',
            position: 'relative',
            zIndex: 1 // Lower z-index than dropdown
          }}>
            <Typography 
              variant="caption" 
              fontWeight="500"
              sx={{ 
                color: '#219297', 
                display: 'block',
                mb: 0.5,
                fontSize: { xs: '0.75rem', sm: '0.8rem' }
              }}
            >
              Selected Timezone:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace', 
                fontWeight: 600, 
                color: '#219297',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                wordBreak: 'break-word'
              }}
            >
              {selectedTimezone}
            </Typography>
            {selectedTimezone && selectedTimezone !== currentTimezone && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  display: 'block', 
                  mt: 0.5,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                Current: {currentTimezone} → New: {selectedTimezone}
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* Sticky footer for mobile devices */}
        <Box 
          sx={{ 
            position: isMobile ? 'sticky' : 'static',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#f8fafa',
            pt: 2,
            mt: 2,
            borderTop: isMobile ? '1px solid #e0e0e0' : 'none',
            zIndex: 10
          }}
        >
          <Box 
            display="flex" 
            gap={1} 
            justifyContent="flex-end" 
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <Button
              size={isMobile ? "medium" : "small"}
              variant="outlined"
              fullWidth={isMobile}
              onClick={() => {
                setIsEditingTimezone(false);
                setSelectedTimezone(currentTimezone);
                setSearchInput('');
                setShowTimezoneList(false);
              }}
              disabled={saving}
              sx={{ 
                fontSize: { xs: '0.85rem', sm: '0.75rem' },
                textTransform: 'none',
                fontWeight: 500,
                color: '#666',
                px: { xs: 2, sm: 2 },
                py: { xs: 1, sm: 0.5 }
              }}
            >
              Cancel
            </Button>
            <Button
              size={isMobile ? "medium" : "small"}
              variant="contained"
              fullWidth={isMobile}
              onClick={updateTimezone}
              disabled={saving || !timezonesLoaded || selectedTimezone === currentTimezone || !selectedTimezone}
              startIcon={saving ? <CircularProgress size={isMobile ? 18 : 16} /> : null}
              data-save-timezone="true"
              sx={{ 
                fontSize: { xs: '0.85rem', sm: '0.75rem' },
                textTransform: 'none',
                fontWeight: 500,
                backgroundColor: '#219297',
                px: { xs: 2, sm: 2 },
                py: { xs: 1, sm: 0.5 },
                color: "#ffffff",
                '&:hover': {
                  backgroundColor: '#197f85',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#999'
                }
              }}
            >
              {saving ? 'Updating...' : 'Save Timezone'}
            </Button>
          </Box>
          
          {/* Mobile helper text */}
          {isMobile && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#666', 
                display: 'block', 
                textAlign: 'center',
                mt: 1,
                fontSize: '0.7rem'
              }}
            >
              Tap search box to see timezones, tap one to select
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
  
  return (
    <Box
      onMouseEnter={() => !isMobile && setIsHovering(true)}
      onMouseLeave={() => !isMobile && setIsHovering(false)}
      onClick={() => setIsEditingTimezone(true)}
      sx={{
        p: { xs: 1.5, sm: 2 },
        backgroundColor: isHovering ? '#f0f7fa' : '#f8fafa',
        borderRadius: '10px',
        border: `1px solid ${isHovering ? '#219297' : '#e0e0e0'}`,
        mb: 3,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(33, 146, 151, 0.1)',
          '& .timezone-edit-icon': {
            opacity: 1,
            transform: 'translateX(0)'
          }
        },
        '&:active': isMobile && {
          backgroundColor: '#e8f3f4',
          transform: 'scale(0.99)',
        }
      }}
    >
      {/* Background accent */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          backgroundColor: '#219297',
          borderTopLeftRadius: '10px',
          borderBottomLeftRadius: '10px'
        }}
      />
      
      <Box display="flex" alignItems="center" gap={2} sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: '8px',
            backgroundColor: '#21929715',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <AccessTime sx={{ 
            color: '#219297', 
            fontSize: { xs: 16, sm: 18 } 
          }} />
        </Box>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5} flexWrap="wrap">
            <Typography 
              variant="subtitle2" 
              fontWeight="600" 
              sx={{ 
                color: '#333', 
                fontSize: { xs: '0.85rem', sm: '0.9rem' } 
              }}
            >
              Company Timezone
            </Typography>
            <Box 
              className="timezone-edit-icon"
              sx={{ 
                opacity: isHovering || isMobile ? 1 : 0,
                transform: (isHovering || isMobile) ? 'translateX(0)' : 'translateX(10px)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#219297', 
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                {isMobile ? 'Tap to edit' : 'Edit'}
              </Typography>
              <Edit sx={{ 
                color: '#219297', 
                fontSize: { xs: 14, sm: 16 } 
              }} />
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#219297', 
                fontWeight: 600, 
                fontFamily: 'monospace',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                wordBreak: 'break-word'
              }}
            >
              {isMobile && currentTimezone.length > 20 
                ? currentTimezone.split('/').pop()?.replace(/_/g, ' ') || currentTimezone
                : currentTimezone}
            </Typography>
            
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: (isHovering || isMobile) ? '#219297' : '#666'
                }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (isHovering || isMobile) ? '#219297' : '#666',
                  fontSize: { xs: '0.75rem', sm: '0.8rem' }
                }}
              >
                {getCurrentTime(currentTimezone)}
              </Typography>
            </Box>
            
            <Chip
              label={getTimezoneOffset(currentTimezone) || 'UTC'}
              size="small"
              sx={{ 
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                height: { xs: '16px', sm: '18px' },
                backgroundColor: '#e0f7fa',
                color: '#219297',
                fontWeight: 500
              }}
            />
          </Box>
          
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#666', 
              mt: 0.5, 
              display: 'block', 
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}
          >
            {isMobile ? 'Tap to change timezone' : 'Click to change timezone for all team scheduling'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
  if (loading) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }
        }}
      >
        <DialogContent sx={{ p: 6 }}>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
            <CircularProgress sx={{ color: "#219297" }} size={48} />
            <Typography variant="body1" sx={{ color: "#666", fontWeight: 400 }}>
              Loading team data...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 3, 
        pb: 2,
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: '#21929715',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Groups sx={{ color: '#219297', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', fontSize: '1.1rem' }}>
                Team Management
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Manage team members and scheduling settings
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: '#f0f0f0',
          px: 3,
          pt: 2
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={activeTab === 'team-members' ? 'contained' : 'text'}
              onClick={() => setActiveTab('team-members')}
              startIcon={<Groups />}
              sx={{ 
                borderRadius: '8px 8px 0 0',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 2,
                py: 1,
                backgroundColor: activeTab === 'team-members' ? '#219297' : 'transparent',
                color: activeTab === 'team-members' ? '#fff' : '#666',
                '&:hover': {
                  backgroundColor: activeTab === 'team-members' ? '#197f85' : '#f5f5f5',
                }
              }}
            >
              Team Members
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'contained' : 'text'}
              onClick={() => setActiveTab('settings')}
              startIcon={<Settings />}
              sx={{ 
                borderRadius: '8px 8px 0 0',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 2,
                py: 1,
                backgroundColor: activeTab === 'settings' ? '#219297' : 'transparent',
                color: activeTab === 'settings' ? '#fff' : '#666',
                '&:hover': {
                  backgroundColor: activeTab === 'settings' ? '#197f85' : '#f5f5f5',
                }
              }}
            >
              Global Settings
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        <Box sx={{ px: 3, pt: 2 }}>
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2,
                borderRadius: '10px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #c8e6c9',
                '& .MuiAlert-icon': {
                  color: '#2e7d32'
                }
              }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: '10px',
                backgroundColor: '#ffebee',
                border: '1px solid #ef9a9a',
                '& .MuiAlert-icon': {
                  color: '#d32f2f'
                }
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Box>

        {/* Content Area */}
        <Box sx={{ px: 3, py: 2, minHeight: 400, maxHeight: 'calc(80vh - 200px)', overflowY: 'auto' }}>
          {/* Timezone Display - Compact and Beautiful */}
          <TimezoneDisplay />
          
          {activeTab === 'team-members' && (
            <Box>
              {/* Current Team Members section remains the same */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                    Current Team Members
                  </Typography>
                  <Chip
                    label={`${teamMembers.length} members`}
                    size="small"
                    sx={{
                      backgroundColor: '#21929715',
                      color: '#219297',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>
                
                {teamMembers.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {teamMembers.map((member, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: '1px solid #f0f0f0',
                          backgroundColor: '#fafafa',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderColor: '#21929730',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: member.is_night_shift ? '#4527a0' : '#219297',
                                fontSize: '0.9rem',
                                fontWeight: 600
                              }}
                            >
                              {member.name?.[0]?.toUpperCase() || 'T'}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                  {member.name || member.email || member.calendar_id}
                                </Typography>
                                {member.is_night_shift && <NightShiftBadge isNightShift={true} size="small" />}
                              </Box>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                                {member.email || member.calendar_id}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <PriorityEditor 
                                  member={member}
                                  saving={saving}
                                  onUpdatePriority={handleUpdatePriority}
                                />
                                {/* <Typography variant="caption" sx={{ color: '#666' }}>
                                  {formatWorkingHours(member.working_hours_start, member.working_hours_end)}
                                </Typography> */}
                              </Box>
                              
                              <MemberWorkingHours
                                member={member}
                                editingMemberId={editingMemberId}
                                editingMemberSettings={editingMemberSettings}
                                setEditingMemberSettings={setEditingMemberSettings}
                                onUpdateMemberSettings={handleUpdateMemberSettings}
                                onStartEditing={startEditingMember}
                                onCancelEditing={cancelEditingMember}
                                saving={saving}
                              />
                            </Box>
                          </Box>
                          
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (window.confirm(`Remove ${member.name || member.email || member.calendar_id}?`)) {
                                handleRemoveTeamMember(member.calendar_id || member.email);
                              }
                            }}
                            sx={{
                              color: '#d32f2f',
                              '&:hover': {
                                backgroundColor: '#ffebee'
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Paper sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: '1px dashed #e0e0e0',
                    backgroundColor: '#fafafa'
                  }}>
                    <Groups sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      No team members yet. Add members below.
                    </Typography>
                  </Paper>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Add Team Member section remains the same */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2, fontSize: '0.95rem' }}>
                  Add Team Member
                </Typography>
                
                {availableCalendars.length > 0 ? (
                  <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                            Select Calendar
                          </Typography>
                          <Select
                            value={selectedCalendar}
                            onChange={(e) => setSelectedCalendar(e.target.value)}
                            displayEmpty
                            sx={{
                              borderRadius: '8px',
                              backgroundColor: '#fff',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e0e0e0'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#219297'
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em style={{ color: '#999' }}>Select a calendar...</em>
                            </MenuItem>
                            {availableCalendars.map((calendar) => {
                              const calendarValue = calendar.id || calendar.email;
                              if (!calendarValue) return null;
                              
                              return (
                                <MenuItem key={calendarValue} value={calendarValue}>
                                  <Box display="flex" alignItems="center" gap={1.5}>
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#21929715' }}>
                                      <CalendarToday sx={{ fontSize: 16, color: '#219297' }} />
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {calendar?.name || calendar?.summary || calendar?.email || calendar?.id}
                                      </Typography>
                                      {calendar?.email && (
                                        <Typography variant="caption" color="textSecondary">
                                          {calendar.email}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </MenuItem>
                              );
                            }).filter(Boolean)}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                          Priority Level
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={newMemberPriority}
                            onChange={(e) => setNewMemberPriority(e.target.value)}
                            sx={{
                              borderRadius: '8px',
                              backgroundColor: '#fff',
                            }}
                          >
                            {priorityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: getPriorityColor(option.value),
                                    }}
                                  />
                                  <Typography variant="body2">{option.label}</Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                          Working Hours
                        </Typography>
                        <Box display="flex" gap={1} alignItems="center">
                          <CustomInput
                            type="time"
                            value={newMemberSettings.working_hours_start}
                            onChange={(e) => setNewMemberSettings(prev => ({
                              ...prev,
                              working_hours_start: e.target.value
                            }))}
                            style={{ flex: 1 }}
                          />
                          <Typography variant="caption" sx={{ color: '#666' }}>to</Typography>
                          <CustomInput
                            type="time"
                            value={newMemberSettings.working_hours_end}
                            onChange={(e) => setNewMemberSettings(prev => ({
                              ...prev,
                              working_hours_end: e.target.value
                            }))}
                            style={{ flex: 1 }}
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                          Working Days
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                            <Chip
                              key={day}
                              label={day.slice(0, 3).toUpperCase()}
                              size="small"
                              onClick={() => {
                                const days = [...newMemberSettings.working_days];
                                if (days.includes(day)) {
                                  setNewMemberSettings(prev => ({
                                    ...prev,
                                    working_days: days.filter(d => d !== day)
                                  }));
                                } else {
                                  setNewMemberSettings(prev => ({
                                    ...prev,
                                    working_days: [...days, day]
                                  }));
                                }
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                height: '24px',
                                cursor: 'pointer',
                                backgroundColor: newMemberSettings.working_days.includes(day) ? '#219297' : '#e0e0e0',
                                color: newMemberSettings.working_days.includes(day) ? '#fff' : '#666',
                                '&:hover': {
                                  backgroundColor: newMemberSettings.working_days.includes(day) ? '#197f85' : '#d0d0d0',
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddTeamMember}
                      disabled={!selectedCalendar || saving}
                      startIcon={saving ? <CircularProgress size={16} /> : <Add />}
                      sx={{
                        mt: 1,
                        backgroundColor: '#219297',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        color:'#ffffff',
                        py: 1.2,
                        '&:hover': {
                          backgroundColor: '#197f85'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#fdfdfdff'
                        }
                      }}
                    >
                      {saving ? 'Adding Member...' : 'Add Team Member'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            ) : (
              <Paper sx={{ 
                p: 3, 
                textAlign: 'center',
                borderRadius: '12px',
                border: '1px dashed #e0e0e0'
              }}>
                <InfoOutlined sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                <Typography variant="body2" sx={{ color: '#999' }}>
                  No additional calendars available. Share calendars with your account to add team members.
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 'settings' && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2, fontSize: '0.95rem' }}>
            Global Scheduling Configuration
          </Typography>
          
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: '10px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #90caf9'
            }}
          >
            <Typography variant="caption">
              These are global defaults. Individual team members can override with their own working hours.
            </Typography>
          </Alert>

          <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #f0f0f0' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                  Slot Duration
                </Typography>
                <CustomInput
                  type="number"
                  value={newConfig.slot_duration_minutes}
                  onChange={(e) => setNewConfig({
                    ...newConfig,
                    slot_duration_minutes: parseInt(e.target.value) || 30
                  })}
                  placeholder="Minutes"
                  startIcon={<AccessTime sx={{ fontSize: 18 }} />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                  Maximum Booking Days
                </Typography>
                <CustomInput
                  type="number"
                  value={newConfig.max_booking_days}
                  onChange={(e) => setNewConfig({
                    ...newConfig,
                    max_booking_days: parseInt(e.target.value) || 90
                  })}
                  placeholder="Days"
                  startIcon={<CalendarMonth sx={{ fontSize: 18 }} />}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : null}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#219297',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#197f85'
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  </DialogContent>

  <DialogActions sx={{ 
    p: 3, 
    pt: 2,
    borderTop: '1px solid #f0f0f0',
    gap: 1
  }}>
    <Button 
      onClick={onClose}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        color: '#666',
        px: 3,
        '&:hover': {
          backgroundColor: '#f5f5f5'
        }
      }}
    >
      Close
    </Button>
  </DialogActions>
</Dialog>
);
});
const useGoogleCalendarStatus = (domainId) => {
  const [hasGoogleCredentials, setHasGoogleCredentials] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checkGoogleCredentials = useCallback(async () => {
    if (!domainId) return false;
    
    try {
      setIsLoading(true);
      const res = await axios.get(`${FAST_API_BASE_URL}/api/mcp-credentials/google-calendar`, {
        headers: { "DOMAIN-TOKEN": domainId },
      }).catch(() => ({ data: { result: null } }));
      
      const credentials = res?.data?.result;
      const hasValidCredentials = credentials?.client_id && 
                                 credentials?.client_secret && 
                                 credentials.client_id.trim() !== '' && 
                                 credentials.client_secret.trim() !== '';
      
      setHasGoogleCredentials(hasValidCredentials);
      return hasValidCredentials;
    } catch (err) {
      console.error("Failed to check Google credentials:", err);
      setHasGoogleCredentials(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [domainId]);

  return {
    hasGoogleCredentials,
    isLoading,
    checkGoogleCredentials,
    setHasGoogleCredentials
  };
};

const useSingleProvider = (domainId) => {
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);
  const [calendarUrl, setCalendarUrl] = useState("");
  const [hasExistingConnection, setHasExistingConnection] = useState(false);
  const [existingProvider, setExistingProvider] = useState("");
  const [existingUrl, setExistingUrl] = useState("");
  const [providerHistory, setProviderHistory] = useState({});
  const [loading, setLoading] = useState({ 
    fetch: false, 
    save: false, 
    check: false, 
    disconnect: false 
  });

  useEffect(() => {
    if (!domainId) {
      setSelectedProvider(DEFAULT_PROVIDER);
      setCalendarUrl("");
      setHasExistingConnection(false);
      setExistingProvider("");
      setExistingUrl("");
      setProviderHistory({});
      setLoading({ fetch: false, save: false, check: false, disconnect: false });
    }
  }, [domainId]);

const fetchActiveProvider = useCallback(async () => {
  if (!domainId) return;

  try {
    setLoading(prev => ({ ...prev, check: true }));
    const res = await axios.get(
      `${FAST_API_BASE_URL}/api/calendar-providers`,
      { headers: { "DOMAIN-TOKEN": domainId } }
    );

    if (res?.data?.resultCode === "0" && res.data.result?.provider) {
      const { provider, calendar_url } = res.data.result;
      setHasExistingConnection(true);
      setExistingProvider(provider);
      setExistingUrl(calendar_url);
      setSelectedProvider(provider);
      setCalendarUrl(calendar_url);
      setProviderHistory(prev => ({ ...prev, [provider]: calendar_url }));
    } else {
      setSelectedProvider(DEFAULT_PROVIDER);
      setHasExistingConnection(false);
      setExistingProvider("");
      setExistingUrl("");
    }
  } catch (err) {
    console.error("Failed to fetch active provider:", err);
    setSelectedProvider(DEFAULT_PROVIDER);
    setHasExistingConnection(false);
    setExistingProvider("");
    setExistingUrl("");
  } finally {
    setLoading(prev => ({ ...prev, check: false }));
  }
}, [domainId]);

  const fetchProviderDetails = useCallback(async (providerId) => {
    if (!domainId || !providerId || providerId === existingProvider) return;

    try {
      setLoading(prev => ({ ...prev, fetch: true }));
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/calendar-providers/${providerId}`,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );

      if (res?.data?.resultCode === "0" && res.data.result?.calendar_url) {
        setCalendarUrl(res.data.result.calendar_url);
        setProviderHistory(prev => ({ ...prev, [providerId]: res.data.result.calendar_url }));
      } else {
        setCalendarUrl("");
        setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
      }
    } catch (err) {
      console.error(`Failed to fetch provider details for ${providerId}:`, err);
      setCalendarUrl("");
      setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  }, [domainId, existingProvider]);

  const saveProvider = useCallback(async (provider, url) => {
    if (provider === TEAM_SCHEDULING_PROVIDER) {
      url = "";
    } else if (!provider || !url.trim()) {
      return { success: false, message: "Please select a provider and enter a valid URL" };
    }

    try {
      setLoading(prev => ({ ...prev, save: true }));
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        { provider, calendar_url: url.trim()|| "N/A" },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(true);
        setExistingProvider(provider);
        setExistingUrl(url.trim());
        setProviderHistory(prev => ({ ...prev, [provider]: url.trim() }));
        return { success: true, message: "Provider connected successfully!" };
      } else {
        throw new Error(res?.data?.messages || "Failed to save provider");
      }
    } catch (err) {
      console.error("Failed to save provider:", err);
      return { 
        success: false, 
        message: err.response?.data?.messages || "Failed to connect. Please try again." 
      };
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  }, [domainId]);

  const disconnectProvider = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, disconnect: true }));
      const res = await axios.delete(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
          data: { provider: existingProvider },
        }
      );

      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(false);
        setExistingProvider("");
        setExistingUrl("");
        setProviderHistory(prev => ({ ...prev, [existingProvider]: "" }));
        setSelectedProvider(DEFAULT_PROVIDER);
        setCalendarUrl("");
        return { success: true, message: "Disconnected successfully!" };
      } else {
        throw new Error(res?.data?.messages || "Failed to disconnect");
      }
    } catch (err) {
      console.error("Failed to disconnect:", err);
      return { 
        success: false, 
        message: err.response?.data?.messages || "Failed to disconnect. Please try again." 
      };
    } finally {
      setLoading(prev => ({ ...prev, disconnect: false }));
    }
  }, [existingProvider, domainId]);

  return {
    selectedProvider,
    setSelectedProvider,
    calendarUrl,
    setCalendarUrl,
    hasExistingConnection,
    existingProvider,
    existingUrl,
    loading,
    fetchActiveProvider,
    fetchProviderDetails,
    saveProvider,
    disconnectProvider
  };
};

const useTeamScheduling = (domainId) => {
  const [teamSchedulingStatus, setTeamSchedulingStatus] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isTeamSchedulingEnabled, setIsTeamSchedulingEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [schedulingSettings, setSchedulingSettings] = useState({
    slot_duration_minutes: 30,
    buffer_before_minutes: 5,
    buffer_after_minutes: 5,
    min_lead_time_hours: 2,
    max_booking_days: 90,
    timezone: 'UTC',
  });

  useEffect(() => {
    if (!domainId) {
      setTeamSchedulingStatus(null);
      setTeamMembers([]);
      setIsTeamSchedulingEnabled(false);
      setSchedulingSettings({
        slot_duration_minutes: 30,
        buffer_before_minutes: 5,
        buffer_after_minutes: 5,
        min_lead_time_hours: 2,
        max_booking_days: 90,
        timezone: 'UTC',
      });
    }
  }, [domainId]);

  const fetchTeamSchedulingStatus = useCallback(async () => {
    if (!domainId) return;
    
    try {
      setIsLoading(true);
      const res = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/status`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      if (res.data.success) {
        setTeamSchedulingStatus(res.data.data);
        setIsTeamSchedulingEnabled(res.data.data.can_accept_bookings);
        const members = res.data.data.team_members || [];
        const membersWithNightShift = members.map(member => ({
          ...member,
          is_night_shift: isNightShift(member.working_hours_start, member.working_hours_end)
        }));
        setTeamMembers(membersWithNightShift);
        
        if (res.data.data.config) {
          setSchedulingSettings(res.data.data.config);
        }
      } else {
        setTeamSchedulingStatus(null);
        setIsTeamSchedulingEnabled(false);
      }
    } catch (err) {
      console.error("Failed to fetch team scheduling status:", err);
      setTeamSchedulingStatus(null);
      setIsTeamSchedulingEnabled(false);
    } finally {
      setIsLoading(false);
    }
  }, [domainId]);

  return {
    teamSchedulingStatus,
    teamMembers,
    isTeamSchedulingEnabled,
    setIsTeamSchedulingEnabled,
    schedulingSettings,
    setSchedulingSettings,
    fetchTeamSchedulingStatus,
    isLoading
  };
};

const useGoogleOAuth = (domainId, onSuccess) => {
  const [oauthLoading, setOauthLoading] = useState(false);
  const popupCheckIntervalRef = useRef(null);
  const messageListenerRef = useRef(null);

  const initiateOAuth = useCallback(async () => {
    if (!domainId) return;
    
    setOauthLoading(true);
    
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/google-calendar/auth`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      if (res?.data?.result?.auth_url) {
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          res.data.result.auth_url,
          'google_calendar_auth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,menubar=no,scrollbars=yes,resizable=yes`
        );
        
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          setOauthLoading(false);
          return { success: false, needsPopupHelp: true };
        }

        const handleMessage = (event) => {
          const validOrigins = [
            'http://localhost:8000',
            'http://localhost:3000',
            'https://api.ansera.ai',
          ];
          
          if (!validOrigins.includes(event.origin)) return;
          
          if (event.data.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
            if (messageListenerRef.current) {
              window.removeEventListener('message', messageListenerRef.current);
              messageListenerRef.current = null;
            }
            
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
            
            if (popup && !popup.closed) {
              popup.close();
            }
            
            setOauthLoading(false);
            onSuccess?.();
            return { success: true };
          } else if (event.data.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
            setOauthLoading(false);
            return { success: false, message: event.data.message };
          }
        };
        
        messageListenerRef.current = handleMessage;
        window.addEventListener('message', messageListenerRef.current);
        
        popupCheckIntervalRef.current = setInterval(() => {
          try {
            if (popup.closed) {
              if (popupCheckIntervalRef.current) {
                clearInterval(popupCheckIntervalRef.current);
                popupCheckIntervalRef.current = null;
              }
              
              if (messageListenerRef.current) {
                window.removeEventListener('message', messageListenerRef.current);
                messageListenerRef.current = null;
              }
              
              if (oauthLoading) {
                setOauthLoading(false);
              }
            }
          } catch (e) {
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
          }
        }, 1000);
        
        return { success: true };
      } else {
        throw new Error("No auth URL received");
      }
    } catch (err) {
      console.error("Failed to initiate Google auth:", err);
      setOauthLoading(false);
      return { success: false, message: "Failed to connect to Google Calendar." };
    }
  }, [domainId, onSuccess]);

  useEffect(() => {
    return () => {
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current);
        messageListenerRef.current = null;
      }
      
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }
    };
  }, []);

  return {
    oauthLoading,
    initiateOAuth
  };
};

const Integrations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  
  const [activeTab, setActiveTab] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: "",
    data: null,
  });
  const [teamManagementModalOpen, setTeamManagementModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showPopupHelp, setShowPopupHelp] = useState(false);
  const [isSavingTeamProvider, setIsSavingTeamProvider] = useState(false);
  
  const googleStatus = useGoogleCalendarStatus(domainId);
  const singleProvider = useSingleProvider(domainId);
  const teamScheduling = useTeamScheduling(domainId);
  
  const googleOAuth = useGoogleOAuth(domainId, () => {
    googleStatus.checkGoogleCredentials();
    
    if (activeTab === 1 || singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
      teamScheduling.fetchTeamSchedulingStatus();
    }
    
    setSnackbar({
      open: true,
      severity: "success",
      message: "Google Calendar connected successfully!",
    });
  });

  const getEmbedUrlForProvider = useCallback((providerId) => {
    const shareUrl = LOOM_SHARE_URLS[providerId];
    if (!shareUrl) return null;
    return getLoomEmbedUrl(shareUrl);
  }, []);

  const providerHasVideo = useCallback((providerId) => {
    return calendarProviders.find(p => p.id === providerId)?.hasVideo && LOOM_SHARE_URLS[providerId];
  }, []);

  const validateUrl = useCallback((provider, url) => {
    const trimmedUrl = url.trim().toLowerCase();
    
    if (provider === TEAM_SCHEDULING_PROVIDER) {
      return "";
    }
    
    if (!trimmedUrl) {
      return "URL is required";
    }

    try {
      new URL(trimmedUrl);
    } catch (err) {
      return "Please enter a valid URL starting with http:// or https://";
    }

    const providerPatterns = {
      "google-calendar": [/calendar\.google\.com/, /calendar\.app\.google/],
      "calendly": [/calendly\.com/],
      "zoho-calendar": [/calendar\.zoho\.com/, /zoho\.com\/calendar/],
      "contact-form": [],
      "team-scheduling": []
    };

    const patterns = providerPatterns[provider];
    
    if (patterns && patterns.length > 0) {
      const isValid = patterns.some(pattern => pattern.test(trimmedUrl));
      if (!isValid) {
        const providerName = calendarProviders.find(p => p.id === provider)?.name || provider;
        return `Please enter a valid ${providerName} URL`;
      }
    }

    return "";
  }, []);

useEffect(() => {
  if (domainId) {
    const initializeIntegrations = async () => {
      try {
        await singleProvider.fetchActiveProvider();
        
        googleStatus.checkGoogleCredentials();
        if (singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
          await teamScheduling.fetchTeamSchedulingStatus();
        }
        
        console.log('Initialization complete. Provider:', 
          singleProvider.existingProvider
        );
      } catch (error) {
        console.error('Failed to initialize integrations:', error);
      }
    };
    
    initializeIntegrations();
  }
}, [domainId]);

useEffect(() => {
  if (singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
    setActiveTab(1);
  }
}, [singleProvider.existingProvider]);

useEffect(() => {
  if (activeTab === 1 && domainId && googleStatus.hasGoogleCredentials) {
    teamScheduling.fetchTeamSchedulingStatus();
  }
}, [activeTab, domainId, googleStatus.hasGoogleCredentials]);

  useEffect(() => {
    if (singleProvider.selectedProvider && singleProvider.selectedProvider !== singleProvider.existingProvider) {
      const timer = setTimeout(() => {
        singleProvider.fetchProviderDetails(singleProvider.selectedProvider);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [singleProvider.selectedProvider]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setShowVideo(false);
    setCurrentVideoUrl("");
  }, []);

  const handleProviderChange = useCallback((event) => {
    const providerId = event.target.value;
    singleProvider.setSelectedProvider(providerId);
    setUrlError("");
    setShowVideo(false);
    setCurrentVideoUrl("");
  }, [singleProvider]);

  const handleUrlChange = useCallback((e) => {
    const value = e.target.value;
    singleProvider.setCalendarUrl(value);
    if (singleProvider.selectedProvider) {
      const error = validateUrl(singleProvider.selectedProvider, value);
      setUrlError(error);
    }
  }, [singleProvider.selectedProvider, validateUrl, singleProvider]);

  const openProviderWebsite = useCallback(() => {
    const urls = {
      "google-calendar": "https://calendar.google.com/calendar/u/0/r",
      "calendly": "https://calendly.com",
      "zoho-calendar": "https://calendar.zoho.com",
    };
    if (singleProvider.selectedProvider && urls[singleProvider.selectedProvider]) {
      window.open(urls[singleProvider.selectedProvider], "_blank");
    }
  }, [singleProvider.selectedProvider]);

  const handleWatchVideo = useCallback(() => {
    const embedUrl = getEmbedUrlForProvider(singleProvider.selectedProvider);
    if (embedUrl) {
      setCurrentVideoUrl(embedUrl);
      setShowVideo(true);
      
      if (isMobile) {
        setTimeout(() => {
          document.querySelector('.video-section')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [singleProvider.selectedProvider, getEmbedUrlForProvider, isMobile]);

  const handleConnectGoogle = useCallback(async () => {
    const result = await googleOAuth.initiateOAuth();
    
    if (result.needsPopupHelp) {
      setShowPopupHelp(true);
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Popup was blocked. Please enable popups for this site.",
      });
    } else if (!result.success) {
      setSnackbar({
        open: true,
        severity: "error",
        message: result.message || "Failed to connect Google Calendar.",
      });
    }
  }, [googleOAuth]);

  const handleSaveTeamSchedulingProvider = useCallback(async () => {
    setIsSavingTeamProvider(true);
    const result = await singleProvider.saveProvider(TEAM_SCHEDULING_PROVIDER, "");
    
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.success ? "Team scheduling enabled !" : result.message,
    });
    
    if (result.success) {
      setActiveTab(1);
      await teamScheduling.fetchTeamSchedulingStatus();
    }
    
    setIsSavingTeamProvider(false);
  }, [singleProvider, teamScheduling]);

  const handleSaveSettings = useCallback(async () => {
    setSavingSettings(true);
    try {
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/config`,
        teamScheduling.schedulingSettings,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Settings saved successfully!",
        });
        setShowSettings(false);
        teamScheduling.fetchTeamSchedulingStatus();
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (err) {
      console.error("Failed to save settings:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to save settings. Please try again.",
      });
    } finally {
      setSavingSettings(false);
    }
  }, [teamScheduling, domainId]);

  const handleEnableTeamScheduling = useCallback(async () => {
    if (!googleStatus.hasGoogleCredentials) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please connect Google Calendar first to use team scheduling.",
      });
      return;
    }

    if (!teamScheduling.teamSchedulingStatus?.is_configured) {
      setTeamManagementModalOpen(true);
    } else {
      await handleSaveTeamSchedulingProvider();
    }
  }, [googleStatus.hasGoogleCredentials, teamScheduling.teamSchedulingStatus, handleSaveTeamSchedulingProvider]);

const handleSaveProvider = useCallback(async () => {
  if (singleProvider.selectedProvider === TEAM_SCHEDULING_PROVIDER) {
    await handleSaveTeamSchedulingProvider();
    return;
  }

  if (!singleProvider.selectedProvider || !singleProvider.calendarUrl.trim()) {
    setSnackbar({
      open: true,
      severity: "error",
      message: "Please select a provider and enter a valid URL",
    });
    return;
  }

  const error = validateUrl(singleProvider.selectedProvider, singleProvider.calendarUrl);
  if (error) {
    setUrlError(error);
    return;
  }

  if (singleProvider.hasExistingConnection) {
    if (singleProvider.existingProvider !== singleProvider.selectedProvider) {
      setConfirmDialog({
        open: true,
        action: "update",
        data: { newProvider: singleProvider.selectedProvider },
      });
      return;
    } else {
      setConfirmDialog({
        open: true,
        action: "update",
        data: { newProvider: singleProvider.selectedProvider },
      });
      return;
    }
  }

  const result = await singleProvider.saveProvider(singleProvider.selectedProvider, singleProvider.calendarUrl);
  setSnackbar({
    open: true,
    severity: result.success ? "success" : "error",
    message: result.message,
  });
}, [singleProvider, validateUrl, handleSaveTeamSchedulingProvider]);

  const handleDisconnect = useCallback(async () => {
    const result = await singleProvider.disconnectProvider();
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.message,
    });
    setConfirmDialog({ open: false, action: "" });
  }, [singleProvider]);

  const isContactFormSelected = singleProvider.selectedProvider === "contact-form";
  const hasVideo = providerHasVideo(singleProvider.selectedProvider);
  const isTeamSchedulingTab = activeTab === 1;
  const isTeamSchedulingActive = singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER;

  if (singleProvider.loading.check && activeTab === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox
          sx={{
            backgroundColor: "#fff",
            p: 3,
            borderRadius: '16px',
            boxShadow: "0px 2px 12px rgba(0,0,0,0.04)",
            minHeight: "500px",
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: "#219297", mb: 2 }} />
            <Typography sx={{ color: "#666" }}>Loading integration settings...</Typography>
          </Box>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        sx={{
          backgroundColor: "#fff",
          p: { xs: 2, md: 3 },
          borderRadius: '16px',
          boxShadow: "0px 2px 12px rgba(0,0,0,0.04)",
          minHeight: "500px",
          width: '100%',
        }}
      >
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, action: "" })}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: 1,
              maxWidth: '90vw',
              width: 'min(500px, 90vw)',
              m: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: "600",
              color: "#219297",
              padding: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontSize: '1.1rem',
            }}
          >
            <Info sx={{ color: "#ff9800", fontSize: 22 }} />
            {confirmDialog.action === "disconnect" ? "Disconnect?" : 
             confirmDialog.action === "update" ? "Update Connection?" :
             "Confirm Action"}
          </DialogTitle>
          <DialogContent sx={{ padding: '8px 16px' }}>
            <DialogContentText
              sx={{ 
                color: "#666", 
                fontSize: '0.9rem', 
                lineHeight: 1.6 
              }}
            >
              {confirmDialog.action === "disconnect" 
                ? "Are you sure you want to disconnect? This will remove all integration settings."
                : "You already have a provider connected. Connecting a new one will replace the existing connection."}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ 
            padding: '12px 16px', 
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              onClick={() => setConfirmDialog({ open: false, action: "" })}
              fullWidth={isMobile}
              sx={{
                color: "#666",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                padding: '8px 16px',
                fontSize: '0.9rem',
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
            onClick={() => {
              if (confirmDialog.action === "disconnect") {
                handleDisconnect();
              } else if (confirmDialog.action === "update") {
                singleProvider.saveProvider(singleProvider.selectedProvider, singleProvider.calendarUrl)
                  .then(result => {
                    setSnackbar({
                      open: true,
                      severity: result.success ? "success" : "error",
                      message: result.message,
                    });
                    setConfirmDialog({ open: false, action: "" });
                  });
              }
            }}
              variant="contained"
              fullWidth={isMobile}
              sx={{
                backgroundColor: "#219297",
                fontWeight: 600,
                textTransform: "none",
                color: "#fff",
                borderRadius: "8px",
                padding: '8px 16px',
                fontSize: '0.9rem',
                "&:hover": {
                  backgroundColor: "#1a767a",
                },
              }}
            >
              {confirmDialog.action === "disconnect" 
                ? "Disconnect"
                : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        <Collapse in={showPopupHelp}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              borderRadius: '16px',
              border: '2px solid #219297',
              backgroundColor: '#e0f5f5',
              boxShadow: '0px 4px 12px rgba(33, 146, 151, 0.15)',
            }}
            icon={<InfoOutlined sx={{ fontSize: 28, color: '#219297' }} />}
            onClose={() => setShowPopupHelp(false)}
          >
            <Typography variant="body1" fontWeight={700} gutterBottom sx={{ color: '#145d61', mb: 2 }}>
              🚫 Popup Blocked - Enable Popups to Continue
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#2c5f5f', mb: 2, lineHeight: 1.6 }}>
              Your browser blocked the authorization window. Please allow popups for this site to connect your Google Calendar.
            </Typography>

            <Box sx={{ 
              backgroundColor: '#fff', 
              borderRadius: '12px', 
              p: 2.5, 
              border: '1px solid #a8d5d8' 
            }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#219297', mb: 2 }}>
                How to Enable Popups:
              </Typography>
              
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.5 } }}>
                <li>
                  <Typography variant="body2" sx={{ color: '#424242' }}>
                    <strong style={{ color: '#219297' }}>Chrome / Edge:</strong> Look for the popup icon 
                    <Box component="span" sx={{ 
                      display: 'inline-block', 
                      mx: 0.5, 
                      px: 1, 
                      py: 0.3, 
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                    }}>
                      <CreditCardOff/>
                    </Box> 
                    in your address bar (top right), click it, and select <strong>"Always allow popups from this site"</strong>
                  </Typography>
                </li>
                
                <li>
                  <Typography variant="body2" sx={{ color: '#424242' }}>
                    <strong style={{ color: '#1a767a' }}>Firefox:</strong> Click the <strong>"Preferences"</strong> or <strong>"Options"</strong> button in the address bar, then select <strong>"Allow popups for this site"</strong>
                  </Typography>
                </li>
                
                <li>
                  <Typography variant="body2" sx={{ color: '#424242' }}>
                    <strong style={{ color: '#0d5457' }}>Safari:</strong> Go to <strong>Safari menu</strong> → <strong>Settings</strong> → <strong>Websites</strong> → <strong>Pop-up Windows</strong>, then set this site to <strong>"Allow"</strong>
                  </Typography>
                </li>
              </Box>
            </Box>
          </Alert>
        </Collapse>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={(props) => <Slide {...props} direction="down" />}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              backgroundColor:
                snackbar.severity === "success" ? "#219297" : 
                snackbar.severity === "error" ? "#d32f2f" :
                snackbar.severity === "info" ? "#1976d2" : "#219297",
              color: "#fff",
              borderRadius: '10px',
              fontSize: '0.9rem',
              maxWidth: '90vw',
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <SoftBox mb={3}>
          <SoftBox display="flex" alignItems="center" gap={2} mb={1}>
            <Box
              sx={{
                borderRadius: '10px',
                p: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img 
                src={AnseraLogo} 
                alt="Ansera Logo" 
                style={{ 
                  width: '48px',
                  height: 'auto'
                }} 
              />
            </Box>
            <SoftTypography 
              variant="h5"
              fontWeight="bold" 
              color="dark"
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Scheduling Integration
            </SoftTypography>
          </SoftBox>
          <SoftTypography
            variant="body2"
            color="text"
            sx={{ 
              opacity: 0.7, 
              fontSize: '0.9rem'
            }}
          >
            Connect your calendar or setup team scheduling
          </SoftTypography>
        </SoftBox>

        <Box sx={{ 
          borderBottom: 1, 
          borderColor: '#e0e0e0', 
          mb: 3,
          '& .MuiTabs-indicator': {
            backgroundColor: '#219297',
            height: '3px',
          }
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', md: '1rem' },
                minWidth: { xs: 'auto', md: 160 },
                padding: { xs: '12px 8px', md: '12px 16px' },
                color: '#666',
                '&.Mui-selected': {
                  color: '#219297',
                },
              }
            }}
          >
            <Tab label="Single Calendar" />
            <Tab label="Team Scheduling" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 1.5, 
                    color: '#219297',
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', md: '0.95rem' }
                  }}
                >
                  Select Calendar Provider
                </Typography>
                
                <FormControl fullWidth>
                  <Select
                    value={singleProvider.selectedProvider}
                    onChange={handleProviderChange}
                    disabled={singleProvider.loading.fetch || singleProvider.loading.check}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#f8fafa',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        borderColor: '#219297',
                      },
                      '&.Mui-focused': {
                        borderColor: '#219297',
                        boxShadow: '0 0 0 3px rgba(33, 146, 151, 0.1)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '8px',
                          marginTop: '8px',
                          boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
                          maxHeight: 400,
                        },
                      },
                    }}
                    renderValue={(selected) => {
                      const provider = calendarProviders.find(p => p.id === selected);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={provider?.logo}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: `${provider?.color || '#219297'}15`,
                              color: provider?.color || '#219297',
                            }}
                          >
                            {provider?.icon}
                          </Avatar>
                          <Typography variant="body1" fontWeight="medium" color="text.primary">
                            {provider?.name || selected}
                          </Typography>
                        </Box>
                      );
                    }}
                  >
                    {calendarProviders
                      .filter(p => p.type === "single-user")
                      .map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Avatar
                            src={provider.logo}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: `${provider.color}15`,
                              color: provider.color,
                            }}
                          >
                            {provider.icon}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight="medium" color="text.primary">
                              {provider.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {provider.description}
                            </Typography>
                          </Box>
                          {singleProvider.hasExistingConnection && singleProvider.existingProvider === provider.id && (
                            <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
                          )}
                          {singleProvider.loading.fetch && singleProvider.selectedProvider === provider.id && (
                            <CircularProgress size={16} sx={{ color: '#219297' }} />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {singleProvider.selectedProvider && singleProvider.selectedProvider !== TEAM_SCHEDULING_PROVIDER && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: '#219297',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', md: '0.95rem' }
                      }}
                    >
                      {providerLabels[singleProvider.selectedProvider] || "Calendar URL"}
                    </Typography>
                    
                    {!isContactFormSelected && (
                      <Button
                        startIcon={<CalendarMonth />}
                        onClick={openProviderWebsite}
                        size="small"
                        sx={{
                          color: '#219297',
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#e0faf3ff',
                            color: '#1a767a',
                          },
                        }}
                      >
                        Open {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name || 'Provider'}
                      </Button>
                    )}
                  </Box>

                  <CustomInput
                    value={singleProvider.calendarUrl}
                    onChange={handleUrlChange}
                    placeholder={providerPlaceholders[singleProvider.selectedProvider] || "Enter your calendar URL"}
                    error={!!urlError}
                    helperText={urlError || (isContactFormSelected 
                      ? "Enter the URL of your contact page or form" 
                      : "Make sure the URL is publicly accessible")}
                    disabled={singleProvider.loading.save || singleProvider.loading.fetch}
                    startIcon={<Link sx={{ color: '#219297' }} />}
                    endAdornment={
                      singleProvider.calendarUrl && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => singleProvider.setCalendarUrl("")}
                            sx={{ p: 0.5 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                          <Tooltip title="Copy URL">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(singleProvider.calendarUrl);
                                setSnackbar({
                                  open: true,
                                  severity: "success",
                                  message: "URL copied to clipboard!",
                                });
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )
                    }
                  />

                  <Box sx={{ 
                    mt: 1, 
                    p: 1.5, 
                    backgroundColor: '#e3f2fd', 
                    borderRadius: '8px',
                    border: '1px solid #bbdefb'
                  }}>
                    <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Example URL:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0d47a1', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {providerExamples[singleProvider.selectedProvider]}
                    </Typography>
                  </Box>

                  {providerPricing[singleProvider.selectedProvider] && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoOutlined sx={{ color: '#666', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Pricing: {providerPricing[singleProvider.selectedProvider]}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                {singleProvider.hasExistingConnection && singleProvider.existingProvider !== TEAM_SCHEDULING_PROVIDER ? (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSaveProvider}
                      disabled={singleProvider.loading.save || !singleProvider.calendarUrl.trim() || !!urlError}
                      sx={{
                        backgroundColor: "#219297",
                        fontWeight: 600,
                        textTransform: "none",
                        color: "#fff",
                        borderRadius: "8px",
                        padding: { xs: "10px 16px", md: "12px 24px" },
                        fontSize: { xs: '0.9rem', md: '0.95rem' },
                        "&:hover": {
                          backgroundColor: "#1a767a",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#999",
                        },
                      }}
                    >
                      {singleProvider.loading.save ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                      ) : singleProvider.existingProvider !== singleProvider.selectedProvider ? (
                        "Update Connection"
                      ) : (
                        "Update URL"
                      )}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                      disabled={singleProvider.loading.disconnect}
                      sx={{
                        borderColor: "#d32f2f",
                        color: "#d32f2f",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: { xs: "10px 16px", md: "12px 24px" },
                        fontSize: { xs: '0.9rem', md: '0.95rem' },
                        "&:hover": {
                          backgroundColor: "#b84758ff",
                          borderColor: "#b71c1c",
                          color: "#ffffff",
                        },
                      }}
                    >
                      {singleProvider.loading.disconnect ? "Disconnecting..." : "Disconnect"}
                    </Button>
                  </>
                ) : singleProvider.selectedProvider === TEAM_SCHEDULING_PROVIDER ? (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      setActiveTab(1);
                    }}
                    sx={{
                      backgroundColor: "#219297",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "#1a767a",
                      },
                    }}
                  >
                    Go to Team Scheduling
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSaveProvider}
                    disabled={singleProvider.loading.save || !singleProvider.calendarUrl.trim() || !!urlError}
                    sx={{
                      backgroundColor: "#219297",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "#1a767a",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#e0e0e0",
                        color: "#999",
                      },
                    }}
                  >
                    {singleProvider.loading.save ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                      `Connect ${calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name || 'Provider'}`
                    )}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              {singleProvider.selectedProvider && singleProvider.selectedProvider !== TEAM_SCHEDULING_PROVIDER && (
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: '12px',
                    backgroundColor: '#f8fafa',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #e0f0f0'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                      src={calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.logo}
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: `${calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.color || '#219297'}15`,
                        color: calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.color || '#219297',
                      }}
                    >
                      {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                        {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.description}
                      </Typography>
                    </Box>
                  </Box>
                  {hasVideo && (
                    <Box className="video-section" sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 1.5 
                      }}>
                        {/* <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            color: '#219297',
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', md: '0.95rem' }
                          }}
                        >
                          Setup Guide Video
                        </Typography> */}
                        <Button
                          startIcon={<PlayCircleOutline />}
                          onClick={handleWatchVideo}
                          size="small"
                          sx={{
                            color: '#219297',
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#e0f7fa',
                            },
                          }}
                        >
                          Watch Guide
                        </Button>
                      </Box>
                      
                      <Collapse in={showVideo}>
                        <Box sx={{ 
                          position: 'relative', 
                          width: '100%', 
                          pt: '56.25%',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#000',
                          mb: 1.5
                        }}>
                          {currentVideoUrl && (
                            <iframe
                              src={currentVideoUrl}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                              }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="Setup Guide Video"
                            />
                          )}
                        </Box>
                      </Collapse>
                      
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        Watch this short guide to learn how to set up your {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name} integration.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        mb: 2, 
                        color: '#219297',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', md: '0.95rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <InfoOutlined fontSize="small" />
                      Setup Instructions
                    </Typography>
                    
                    <Box sx={{ 
                      backgroundColor: '#fff', 
                      borderRadius: '8px', 
                      p: 2.5,
                      border: '1px solid #e0f0f0'
                    }}>
                      {providerInstructions[singleProvider.selectedProvider]?.map((step, index) => (
                        <InstructionStep
                          key={index}
                          number={index + 1}
                          title={step.title}
                        >
                          {step.content}
                        </InstructionStep>
                      ))}

                      {isContactFormSelected && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 1.5, 
                          backgroundColor: '#fff3e0', 
                          borderRadius: '6px',
                          border: '1px solid #ffcc80'
                        }}>
                          <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Important:
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#e65100' }}>
                            Your contact form should include fields for name, email, and message. 
                            Ansera will monitor this page for new inquiries and notify you.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ 
                p: { xs: 2, md: 3 }, 
                borderRadius: '12px', 
                backgroundColor: '#f8fafa',
                border: '1px solid #e0f0f0'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: '#219297',
                      }}
                    >
                      <Groups />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        Team Scheduling
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                        Round-robin scheduling with multiple team members
                      </Typography>
                    </Box>
                  </Box>
                  
                  {isTeamSchedulingActive && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          backgroundColor: "#2e7d32",
                          color: "#fff",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          backgroundColor: '#b41d1dff',
                          color: '#ffffff',
                          "&:hover": {
                            backgroundColor: "#9c091fff",
                            borderColor: "#b71c1c",
                            color: "#ffffff",
                          },
                        }}
                      >
                        Disconnect
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box sx={{ 
                  mb: 3, 
                  p: { xs: 2, md: 2.5 }, 
                  backgroundColor: '#f8fafa', 
                  borderRadius: '12px',
                  border: '1px solid #e0f0f0'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg"
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#fff',
                          color: '#219297',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Google />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                          Google Calendar Connection
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                          Required for team scheduling
                        </Typography>
                      </Box>
                    </Box>
                    {googleStatus.isLoading ? (
                      <CircularProgress size={24} sx={{ color: '#219297' }} />
                    ) : (
                      <Chip
                        label={googleStatus.hasGoogleCredentials ? "Calendar connected" : "Not Connected"}
                        size="small"
                        sx={{
                          backgroundColor: googleStatus.hasGoogleCredentials ? "#2e7d32" : "#757575",
                          color: "#fff",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>

                  {googleStatus.hasGoogleCredentials ? (
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 0 }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: '#2e7d32' }} />
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                          Google Calendar is connected
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#fff3e0', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 0 }
                    }}>
                      <Typography variant="body2" sx={{ color: '#e65100', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                        Google Calendar is not connected
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleConnectGoogle}
                        disabled={googleOAuth.oauthLoading}
                        sx={{
                          backgroundColor: "#219297",
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          padding: '6px 16px',
                          color: "#ffffff",
                          '&:hover': {
                            backgroundColor: "#1a767a",
                            color: "#ffffff",
                          },
                        }}
                      >
                        {googleOAuth.oauthLoading ? "Connecting..." : "Connect Google Calendar"}
                      </Button>
                    </Box>
                  )}
                </Box>

                {googleStatus.hasGoogleCredentials && (
                  <Box>
                    {teamScheduling.isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: "#219297", mr: 2 }} />
                        <Typography sx={{ color: "#666" }}>Loading team scheduling data...</Typography>
                      </Box>
                    ) : teamScheduling.teamSchedulingStatus ? (
                      <Box>
                        <Box sx={{ 
                          mb: 3, 
                          p: 2.5, 
                          backgroundColor: '#e8f5e9', 
                          borderRadius: '8px',
                          border: '1px solid #c8e6c9'
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#2e7d32' }}>
                              Team Scheduling is {teamScheduling.teamSchedulingStatus?.can_accept_bookings ? 'Active' : 'Configured'}
                            </Typography>
                            {teamScheduling.teamMembers.some(m => m.is_night_shift) && (
                              <Chip
                                label={`${teamScheduling.teamMembers.filter(m => m.is_night_shift).length} Night Shifts`}
                                size="small"
                                sx={{ 
                                  backgroundColor: '#4527a0',
                                  color: '#fff',
                                  fontSize: '0.65rem'
                                }}
                              />
                            )}
                          </Box>
                          
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
                            gap: 2,
                            mb: 2 
                          }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                Team Members
                              </Typography>
                              <Typography variant="body2" fontWeight="500" sx={{ color: '#219297' }}>
                                {teamScheduling.teamMembers?.length || 0} members configured
                              </Typography>
                            </Box>
                            
                            <Box>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                Slot Duration
                              </Typography>
                              <Typography variant="body2" fontWeight="500" sx={{ color: '#219297' }}>
                                {teamScheduling.schedulingSettings.slot_duration_minutes} minutes
                              </Typography>
                            </Box>
                            
                            <Box>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                Booking Window
                              </Typography>
                              <Typography variant="body2" fontWeight="500" sx={{ color: '#219297' }}>
                                {teamScheduling.schedulingSettings.max_booking_days} days
                              </Typography>
                            </Box>
                            
                            {/* <Box>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                Buffer Time
                              </Typography>
                              <Typography variant="body2" fontWeight="500" sx={{ color: '#219297' }}>
                                {teamScheduling.schedulingSettings.buffer_before_minutes} min before/after
                              </Typography>
                            </Box> */}
                          </Box>
                          
                          {!isTeamSchedulingActive && (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={handleSaveTeamSchedulingProvider}
                              disabled={isSavingTeamProvider}
                              sx={{
                                backgroundColor: "#219297",
                                fontWeight: 600,
                                textTransform: "none",
                                color: "#fff",
                                borderRadius: "8px",
                                padding: "10px 16px",
                                "&:hover": {
                                  backgroundColor: "#1a767a",
                                },
                              }}
                            >
                              {isSavingTeamProvider ? "Enabling..." : "Enable Team Scheduling"}
                            </Button>
                          )}
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 2,
                          flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => setTeamManagementModalOpen(true)}
                            startIcon={<Groups />}
                            sx={{
                              backgroundColor: "#219297",
                              fontWeight: 600,
                              textTransform: "none",
                              color: "#fff",
                              borderRadius: "8px",
                              padding: "10px 16px",
                            }}
                          >
                            Manage Team Members
                          </Button>
                          
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setShowSettings(!showSettings)}
                            startIcon={<Settings />}
                            sx={{
                              borderColor: "#219297",
                              color: "#219297",
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: "8px",
                              padding: "10px 16px",
                              "&:hover": {
                                borderColor: "#1a767a",
                                color: "#1a767a",
                              },
                            }}
                          >
                            {showSettings ? "Hide Settings" : "Show Settings"}
                          </Button>
                        </Box>
                        
                        <Collapse in={showSettings}>
                          <Box sx={{ mt: 3, p: 2.5, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#219297', mb: 2 }}>
                              Scheduling Settings
                            </Typography>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Slot Duration (minutes)</InputLabel>
                                  <Select
                                    value={teamScheduling.schedulingSettings.slot_duration_minutes}
                                    onChange={(e) => teamScheduling.setSchedulingSettings({
                                      ...teamScheduling.schedulingSettings,
                                      slot_duration_minutes: e.target.value
                                    })}
                                    label="Slot Duration (minutes)"
                                  >
                                    <MenuItem value={15}>15 minutes</MenuItem>
                                    <MenuItem value={30}>30 minutes</MenuItem>
                                    <MenuItem value={45}>45 minutes</MenuItem>
                                    <MenuItem value={60}>60 minutes</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              {/* <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Buffer Before (minutes)</InputLabel>
                                  <Select
                                    value={teamScheduling.schedulingSettings.buffer_before_minutes}
                                    onChange={(e) => teamScheduling.setSchedulingSettings({
                                      ...teamScheduling.schedulingSettings,
                                      buffer_before_minutes: e.target.value
                                    })}
                                    label="Buffer Before (minutes)"
                                  >
                                    <MenuItem value={0}>0 minutes</MenuItem>
                                    <MenuItem value={5}>5 minutes</MenuItem>
                                    <MenuItem value={10}>10 minutes</MenuItem>
                                    <MenuItem value={15}>15 minutes</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid> */}
                              
                              {/* <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Buffer After (minutes)</InputLabel>
                                  <Select
                                    value={teamScheduling.schedulingSettings.buffer_after_minutes}
                                    onChange={(e) => teamScheduling.setSchedulingSettings({
                                      ...teamScheduling.schedulingSettings,
                                      buffer_after_minutes: e.target.value
                                    })}
                                    label="Buffer After (minutes)"
                                  >
                                    <MenuItem value={0}>0 minutes</MenuItem>
                                    <MenuItem value={5}>5 minutes</MenuItem>
                                    <MenuItem value={10}>10 minutes</MenuItem>
                                    <MenuItem value={15}>15 minutes</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                               */}
                              {/* <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Minimum Lead Time (hours)</InputLabel>
                                  <Select
                                    value={teamScheduling.schedulingSettings.min_lead_time_hours}
                                    onChange={(e) => teamScheduling.setSchedulingSettings({
                                      ...teamScheduling.schedulingSettings,
                                      min_lead_time_hours: e.target.value
                                    })}
                                    label="Minimum Lead Time (hours)"
                                  >
                                    <MenuItem value={1}>1 hour</MenuItem>
                                    <MenuItem value={2}>2 hours</MenuItem>
                                    <MenuItem value={4}>4 hours</MenuItem>
                                    <MenuItem value={24}>24 hours</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid> */}
                              
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Max Booking Days</InputLabel>
                                  <Select
                                    value={teamScheduling.schedulingSettings.max_booking_days}
                                    onChange={(e) => teamScheduling.setSchedulingSettings({
                                      ...teamScheduling.schedulingSettings,
                                      max_booking_days: e.target.value
                                    })}
                                    label="Max Booking Days"
                                  >
                                    <MenuItem value={30}>30 days</MenuItem>
                                    <MenuItem value={60}>60 days</MenuItem>
                                    <MenuItem value={90}>90 days</MenuItem>
                                    <MenuItem value={180}>180 days</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                            
                            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                              <Button
                                variant="outlined"
                                onClick={() => setShowSettings(false)}
                                sx={{
                                  color: "#666",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  borderRadius: "8px",
                                  padding: "8px 16px",
                                }}
                              >
                                Cancel
                              </Button>
                              
                              <Button
                                variant="contained"
                                onClick={handleSaveSettings}
                                disabled={savingSettings}
                                sx={{
                                  backgroundColor: "#219297",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  color: "#fff",
                                  borderRadius: "8px",
                                  padding: "8px 16px",
                                  "&:hover": {
                                    backgroundColor: "#1a767a",
                                  },
                                }}
                              >
                                {savingSettings ? "Saving..." : "Save Settings"}
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        backgroundColor: '#f5f5f5', 
                        borderRadius: '8px',
                        border: '1px dashed #e0e0e0'
                      }}>
                        <Groups sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" fontWeight="600" sx={{ color: '#666', mb: 1 }}>
                          Team Scheduling Not Configured
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                          Set up team scheduling to enable round-robin booking with multiple team members
                        </Typography>
                        
                        <Button
                          variant="contained"
                          onClick={handleEnableTeamScheduling}
                          disabled={!googleStatus.hasGoogleCredentials || teamScheduling.isLoading}
                          sx={{
                            backgroundColor: "#219297",
                            fontWeight: 600,
                            textTransform: "none",
                            color: "#fff",
                            borderRadius: "8px",
                            padding: "12px 24px",
                            fontSize: '0.95rem',
                            "&:hover": {
                              backgroundColor: "#1a767a",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#e0e0e0",
                              color: "#999",
                            },
                          }}
                        >
                          {!googleStatus.hasGoogleCredentials ? "Connect Google Calendar First" : "Setup Team Scheduling"}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ 
                p: { xs: 2, md: 2.5 }, 
                borderRadius: '12px', 
                backgroundColor: '#f8fafa',
                border: '1px solid #e0f0f0',
                height: '100%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: '#21929715',
                      color: '#219297',
                    }}
                  >
                    <InfoOutlined />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                      How Team Scheduling Works
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                      Round-robin scheduling with multiple team members
                    </Typography>
                  </Box>
                </Box>
                
                <Box className="video-section" sx={{ mb: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 1.5 
                  }}>
                    {/* <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: '#219297',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', md: '0.95rem' }
                      }}
                    >
                      Setup Guide Video
                    </Typography> */}
                    {LOOM_SHARE_URLS['team-scheduling'] && (
                      <Button
                        startIcon={<PlayCircleOutline />}
                        onClick={() => {
                          const embedUrl = getEmbedUrlForProvider('team-scheduling');
                          if (embedUrl) {
                            setCurrentVideoUrl(embedUrl);
                            setShowVideo(true);
                            
                            if (isMobile) {
                              setTimeout(() => {
                                document.querySelector('.video-section')?.scrollIntoView({ 
                                  behavior: 'smooth',
                                  block: 'center'
                                });
                              }, 100);
                            }
                          }
                        }}
                        size="small"
                        sx={{
                          color: '#219297',
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#e0f7fa',
                          },
                        }}
                      >
                        Watch Guide
                      </Button>
                    )}
                  </Box>
                  
                  <Collapse in={showVideo}>
                    <Box sx={{ 
                      position: 'relative', 
                      width: '100%', 
                      pt: '56.25%',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: '#000',
                      mb: 1.5
                    }}>
                      {currentVideoUrl && (
                        <iframe
                          src={currentVideoUrl}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="Team Scheduling Setup Guide"
                        />
                      )}
                    </Box>
                  </Collapse>
                </Box>
                
                <Box sx={{ 
                  backgroundColor: '#fff', 
                  borderRadius: '8px', 
                  p: 2.5,
                  border: '1px solid #e0f0f0'
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 2, 
                      color: '#219297',
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <InfoOutlined fontSize="small" />
                    Key Features
                  </Typography>
                  
                  {providerInstructions['team-scheduling']?.map((step, index) => (
                    <InstructionStep
                      key={index}
                      number={index + 1}
                      title={step.title}
                    >
                      {step.content}
                    </InstructionStep>
                  ))}
                  
                  <Box sx={{ 
                    mt: 2, 
                    p: 1.5, 
                    backgroundColor: '#e3f2fd', 
                    borderRadius: '6px',
                    border: '1px solid #bbdefb'
                  }}>
                    <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Night Shift Support:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0d47a1', display: 'block', mb: 0.5 }}>
                      • Supports night shifts (18:00 → 03:00)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0d47a1', display: 'block', mb: 0.5 }}>
                      • Working hours can span across midnight
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0d47a1', display: 'block' }}>
                      • Each member can have individual schedule
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    mt: 2, 
                    p: 1.5, 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '6px',
                    border: '1px solid #c8e6c9'
                  }}>
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Benefits:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block', mb: 0.5 }}>
                      • Automatically distributes meetings among team members
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block', mb: 0.5 }}>
                      • Prevents double-booking across team calendars
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block', mb: 0.5 }}>
                      • Respects individual working hours and availability
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block' }}>
                      • Priority-based assignment for team members
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {teamManagementModalOpen && (
          <TeamManagementModal
            open={teamManagementModalOpen}
            onClose={() => {
              setTeamManagementModalOpen(false);
              teamScheduling.fetchTeamSchedulingStatus();
            }}
            domainId={domainId}
            refreshData={teamScheduling.fetchTeamSchedulingStatus}
            initialTeamData={teamScheduling.teamMembers}
          />
        )}
      </SoftBox>
    </DashboardLayout>
  );
};

export default Integrations;